/**
 * OwnerSelect — inline recruiter assignment. A compact select fed by the
 * session-cached team list that PATCHes /api/crm/contacts/:id
 * { owner_user_id } itself (optimistic, reverts on failure). Unassigned reads
 * amber so unrouted leads stand out in the inbox. Safe inside clickable rows.
 *
 * Out-of-office (docs/crm-out-of-office-coverage-plan.md): GET /api/crm/team
 * now decorates each member with `out_today` / `out_until`, and an absent
 * recruiter reads "— Out today, back Mon" in the list. Deliberately a HINT and
 * not a block: assigning a lead to someone who is back tomorrow is a normal,
 * correct thing to do. What must not happen is doing it by accident.
 */
import { useState } from 'react';
import api from '../../services/api';
import { useTeam } from '../lib';
import { returnDayLabel } from './CoverageBadge';

/**
 * The out-of-office decoration the /team endpoint adds to each member. Read
 * structurally rather than off `TeamMember`, so this works whether or not the
 * shared type has been widened yet — and tolerates an older API response that
 * carries neither field (no hint, same behaviour as before the feature).
 */
export interface TeamAvailability {
  out_today?: boolean;
  /** Inclusive LAST day of the absence (YYYY-MM-DD) — back the day after. */
  out_until?: string | null;
}

/**
 * "— Out today, back Mon" for an absent recruiter; '' for everyone else.
 * Text, not color: a native <option> cannot be reliably styled across
 * browsers, and this has to be legible in the one place the mistake happens.
 */
export function outTodaySuffix(member: unknown): string {
  const m = member as TeamAvailability | null | undefined;
  if (!m || m.out_today !== true) return '';
  const back = returnDayLabel(m.out_until);
  return back ? ` — Out today, ${back}` : ' — Out today';
}

interface Props {
  contactId: string;
  value: string | null | undefined;
  onChanged?: (newOwnerId: string | null) => void;
  /** Visually compact (inbox rows) vs full (detail page). */
  compact?: boolean;
  className?: string;
}

export default function OwnerSelect({ contactId, value, onChanged, compact = false, className = '' }: Props) {
  const { team, byId, loading: teamLoading, error: teamError, retry } = useTeam();
  const [current, setCurrent] = useState<string>(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const unassigned = !current;
  const knownOwner = current ? byId.get(current) : undefined;
  // The selected owner being out is worth saying on the closed control too —
  // the option text is only visible while the list is open.
  const currentOutHint = outTodaySuffix(knownOwner);

  // Team list failed to load and we have nothing to offer — show a retry
  // affordance instead of a silently empty dropdown.
  if (teamError && team.length === 0) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); retry(); }}
        title="The recruiter list failed to load — click to retry"
        className={`inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-amber-300 font-medium text-amber-700 transition-colors duration-150 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20 sm:min-h-0 ${
          compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
        } ${className}`}
      >
        ⟳ Retry team list
      </button>
    );
  }

  const change = async (next: string) => {
    if (next === current || saving) return;
    const prev = current;
    setCurrent(next);
    setSaving(true);
    setError(false);
    try {
      await api.patch(`/api/crm/contacts/${contactId}`, { owner_user_id: next || null });
      onChanged?.(next || null);
    } catch {
      setCurrent(prev);
      setError(true);
      setTimeout(() => setError(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="relative inline-flex max-w-full" onClick={(e) => e.stopPropagation()}>
      <select
        value={current}
        disabled={saving || (teamLoading && team.length === 0)}
        onChange={(e) => change(e.target.value)}
        title={
          error
            ? 'Could not update assignment — try again'
            : currentOutHint
              ? `Assign to a recruiter — ${knownOwner?.name || 'the current owner'} is out of office today`
              : 'Assign to a recruiter'
        }
        aria-label="Assigned recruiter"
        className={`min-h-[44px] max-w-full cursor-pointer appearance-none truncate rounded-lg border bg-transparent font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-60 sm:min-h-0 ${
          compact ? 'py-1 pl-2 pr-6 text-xs' : 'py-1.5 pl-3 pr-8 text-sm'
        } ${
          unassigned
            ? 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'
            : 'border-gray-200 text-gray-700 dark:border-gray-600 dark:text-gray-200'
        } ${error ? 'ring-2 ring-red-400' : ''} ${className}`}
      >
        <option value="">Unassigned</option>
        {team.map((m) => (
          <option key={m.id} value={m.id}>{m.name}{outTodaySuffix(m)}</option>
        ))}
        {current && !knownOwner && (
          // The owner's name isn't resolvable yet: still fetching vs. a user
          // who's no longer in the team list.
          <option value={current}>{teamLoading ? 'Loading…' : 'Assigned'}</option>
        )}
      </select>
      <svg
        className="pointer-events-none absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 opacity-50"
        viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
      >
        <path d="M3 4.5 6 7.5 9 4.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
